"use client";

import { motion, useAnimation, useReducedMotion, Transition } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface CopyIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface CopyIconProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  className?: string;
}

const CopyIcon = forwardRef<CopyIconHandle, CopyIconProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
    },
    ref,
  ) => {
    const controls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () =>
          reduced ? controls.start("normal") : controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    // React to isHovered prop changes
    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        controls.start("animate");
      } else if (!isHovered) {
        controls.start("normal");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) controls.start("animate");
    }, [controls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) controls.start("normal");
    }, [controls]);

    const defaultTransition: Transition = {
      type: "spring",
      stiffness: 160,
      damping: 17,
      mass: 1,
    };

    const boxVariants = {
      normal: { translateY: 0, translateX: 0, rotate: 0 },
      animate: { translateY: -3, translateX: -3, rotate: 360 },
    };

    const pathVariants = {
      normal: { x: 0, y: 0 },
      animate: { x: 3, y: 3 },
    };

    return (
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
      >
        <motion.rect
          width="14"
          height="14"
          x="8"
          y="8"
          rx="2"
          ry="2"
          variants={boxVariants}
          animate={controls}
          transition={{
            ...defaultTransition,
            duration: 0.7 * duration,
          }}
        />
        <motion.path
          d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"
          variants={pathVariants}
          animate={controls}
          transition={defaultTransition}
        />
      </motion.svg>
    );
  },
);

CopyIcon.displayName = "CopyIcon";
export { CopyIcon };
